import { API_BASE_URL as BASE } from './apiConfig';
import { createAuthedAxios } from '@/lib/authedAxios';

const client = createAuthedAxios({ baseURL: `${BASE}/api` });

client.interceptors.response.use((res) => {
  if (res.data && typeof res.data === 'object' && 'success' in res.data && 'data' in res.data) {
    const wrapped = res.data as {
      success: boolean;
      message?: string | null;
      data: unknown;
    };

    // Keep message/success for object payloads (e.g. plan change), while unwrapping data.
    if (wrapped.data && typeof wrapped.data === 'object' && !Array.isArray(wrapped.data)) {
      res.data = {
        ...(wrapped.data as Record<string, unknown>),
        success: wrapped.success,
        message: wrapped.message ?? null,
      };
    } else {
      res.data = wrapped.data;
    }
  }
  return res;
});

// ── 타입 ─────────────────────────────────────────────────────

export type PlanCode = 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
export type ChangeType = 'UPGRADE' | 'DOWNGRADE' | 'ADMIN';
export type ChangeStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED';
export type PaymentStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'DONE';
export type PaymentMethod = 'BANK_TRANSFER' | 'CARD';

/** 다운그레이드 불가 사유 */
export type DowngradeReason =
  | 'FREE_PLAN'
  | 'STARTER_PLAN'
  | 'MONTHLY_LIMIT_EXCEEDED'
  | 'PENDING_UPGRADE'
  | 'NOT_OWNER'
  | null;

/** 프롤레이션 계산 요청 */
export interface ProrationRequest {
  monthlyLimit: number;
  currentPlan?: PlanCode;
}

/** 일반 플랜 프롤레이션 계산 응답 */
export interface NormalProrationResponse {
  currentPlan: PlanCode;
  newPlan: PlanCode;
  currentPrice: number;
  newPrice: number;
  remainingDays: number;
  totalDays: number;
  currentProration: number;
  newProration: number;
  prorationAmount: number;
  billingCycleDay: number;
  isFullPayment: boolean;
  warningMessage?: string | null;  // 경고 메시지 (PG 최소 결제 금액 조정 등)
  calculationDetails?: {
    startDate: string;
    endDate: string;
    formula: string;
  };
}

/** 엔터프라이즈 프롤레이션 계산 응답 */
export interface EnterpriseProrationResponse {
  tier: string;
  unitPrice: number;
  price: number;
  proratedAmount?: {
    currentPlanPrice: number;
    newPlanPrice: number;
    remainingDays: number;
    billingStartDate: string;
    billingEndDate: string;
    billingCycleDay: number;
    proratedAmount: number;
    totalPaymentAmount: number;
    isMinimumAmountApplied?: boolean;
    originalProratedAmount: number;
    isFullPayment: boolean;
    formula?: string;
  };
}

/** 프롤레이션 계산 응답 (공용) */
export type ProrationResponse = NormalProrationResponse | EnterpriseProrationResponse;

/** 환불 요청 */
export interface RefundRequest {
  cancelReason?: string;           // 취소 사유
  refundBankName?: string;         // 환불 은행명 (계좌이체인 경우만 필수)
  refundAccountNumber?: string;    // 환불 계좌번호 (계좌이체인 경우만 필수)
  refundAccountHolder?: string;    // 환불 계좌 소유자명 (계좌이체인 경우만 필수)
}

/** 환불 금액 조회 응답 */
export interface RefundAmountResponse {
  refundAmount: number;            // 환불 금액
  remainingDays: number;           // 남은 일수
  calculationDetails: string;      // 계산 상세
}

/** P0-T01: 구독 상태머신 4가지 상태 */
export type SubscriptionStatus = 'FREE' | 'PENDING_UPGRADE' | 'ACTIVE_PAID' | 'PENDING_DOWNGRADE';

/** 플랜 메타 정보 */
export interface PlanMeta {
  code: PlanCode;
  name: string;
  price: number | null;
  monthlyLimit: number | null;
  features: string[];
  badge?: string;
  badgeColor?: string;
  color: string;
  borderColor: string;
  bgColor: string;
}

/** 플랜 메타 정보 목록 */
export const PLANS: PlanMeta[] = [
  {
    code: 'FREE',
    name: '무료',
    price: 0,
    monthlyLimit: 40,
    features: ['월 40건 AI 생성', '기본 기능 제공', 'API 접근'],
    color: 'text-gray-700',
    borderColor: 'border-gray-200',
    bgColor: 'bg-gray-50',
  },
  {
    code: 'STARTER',
    name: '스타트',
    price: 9900,
    monthlyLimit: 1000,
    features: ['월 1,000건 AI 생성', '모든 기능 제공', '서비스 지원', 'API 접근'],
    color: 'text-brand-blue',
    borderColor: 'border-brand-blue-light',
    bgColor: 'bg-blue-50',
  },
  {
    code: 'PROFESSIONAL',
    name: '프로',
    price: 29000,
    monthlyLimit: 5000,
    features: ['월 5,000건 AI 생성', '모든 기능 제공', '우선 지원', 'API 접근'],
    badge: '인기',
    badgeColor: 'bg-brand-orange text-white',
    color: 'text-brand-blue-dark',
    borderColor: 'border-brand-blue',
    bgColor: 'bg-blue-50',
  },
  {
    code: 'ENTERPRISE',
    name: '엔터프라이즈',
    price: null,
    monthlyLimit: null,
    features: ['5천건 초과 단가 할인', '대량 사용 누진 요금제', '맞춤 기능', '전담 매니저', 'SLA 보장'],
    color: 'text-brand-purple',
    borderColor: 'border-brand-purple-light',
    bgColor: 'bg-purple-50',
  },
];

/**
 * GET /api/public/plan-policy 응답 - 인증 불필요(로그인 전 약관 모달에서도 조회 가능).
 * true면 비-엔터프라이즈 다운그레이드가 "다음 달 1일"에 적용됨(I-3), false면
 * "본인 결제 주기 종료일 다음날"에 적용됨. src/lib/downgradePolicyText.ts와 함께 사용.
 */
export interface PlanPolicy {
  nonEnterpriseDowngradeAppliesFirstOfMonth: boolean;
}

/** P0-T01: GET /api/my/subscription 응답 */
export interface SubscriptionInfo {
  subscriptionStatus: SubscriptionStatus;
  currentPlan: PlanCode;
  pendingPlan: PlanCode | null;
  statusChangedAt: string | null;
  canChangePlan: boolean;
  hasPendingUpgrade: boolean;
  effectiveDate: string | null;    // 다음 전환 예정일 (ISO date)
}

/** P0-T01: GET /api/my/subscription/history 항목 */
export interface SubscriptionHistoryItem {
  id: number;
  fromStatus: SubscriptionStatus;
  toStatus: SubscriptionStatus;
  fromPlan: PlanCode | null;
  toPlan: PlanCode | null;
  reason: string;
  changedByType: 'USER' | 'ADMIN' | 'SYSTEM';
  changedById: string | null;
  changedAt: string;
}

export interface SubscriptionHistoryResponse {
  content: SubscriptionHistoryItem[];
  totalElements: number;
  totalPages: number;
  number: number;
}

export interface PlanInfo {
  // 개인 컨텍스트 필드 (팀 소속 시 null)
  currentPlan: PlanCode | null;
  personalPlan: PlanCode | null;
  pendingPlan: PlanCode | null;
  planChangedAt: string | null;
  planExpiryDate: string | null;
  downgradeEffectiveDate: string | null;
  estimatedDowngradeEffectiveDate: string | null;
  monthlyLimit: number;
  usedCount: number;
  canChangePlan: boolean;  // 항상 true (업그레이드 항상 가능)
  canDowngrade: boolean;  // 다운그레이드 가능 여부
  downgradeReason: DowngradeReason;  // 다운그레이드 불가 사유
  changePlanReason?: string | null;  // 요금제 변경 불가 사유 (예: 'ENTERPRISE_ACTIVE')
  changeCountThisMonth: number;
  hasPendingUpgrade: boolean;
  subscriptionStatus?: SubscriptionStatus;

  // 팀 컨텍스트 필드 (개인 소속 시 null)
  isTeamContext: boolean;
  teamId: number | null;
  teamName: string | null;
  role: string | null;
  teamPlan: PlanCode | null;
  teamPendingPlan: PlanCode | null;
  teamMonthlyLimit: number;
  teamUsedCount: number;
  teamCanChangePlan: boolean;  // 항상 true (업그레이드 항상 가능)
  teamCanDowngrade: boolean;  // 다운그레이드 가능 여부
  teamDowngradeReason: DowngradeReason;  // 다운그레이드 불가 사유
  teamChangePlanReason?: string | null;  // 팀 요금제 변경 불가 사유 (예: 'ENTERPRISE_ACTIVE')
  teamChangeCountThisMonth: number;
  teamHasPendingUpgrade: boolean;
  teamDowngradeEffectiveDate: string | null;
  teamEstimatedDowngradeEffectiveDate: string | null;
  billingCycleDay: number | null;
  billingStartDate: string | null;
  billingEndDate: string | null;
  enterpriseContract: EnterpriseContract | null;         // 개인 컨텍스트 전용 (팀 소속 시 null)
  teamEnterpriseContract: EnterpriseContract | null;      // 팀 컨텍스트 전용 (개인 소속 시 null)
}

export interface UsageInfo {
  // 개인 컨텍스트 필드 (팀 소속 시 0)
  usedCount: number;
  monthlyLimit: number;
  remaining: number;
  usageRate: number;
  billingMonth: string;
  billingStartDate: string;
  billingEndDate: string;
  planExpiryDate: string | null;
  currentPlan: PlanCode | null;

  // 팀 컨텍스트 필드 (개인 소속 시 0)
  isTeamContext: boolean;
  teamId: number | null;
  teamName: string | null;
  role: string | null;
  teamUsedCount: number;
  teamMonthlyLimit: number;
  teamRemaining: number;
  teamUsageRate: number;
  teamTotalUsed: number | null;
  teamCurrentPlan: PlanCode | null;
  enterpriseContract: EnterpriseContract | null;         // 개인 컨텍스트 전용 (팀 소속 시 null)
  teamEnterpriseContract: EnterpriseContract | null;      // 팀 컨텍스트 전용 (개인 소속 시 null)
}

export interface PlanChangeRequest {
  targetPlan: PlanCode;
  paymentMethod?: PaymentMethod;  // 결제 방법 (BANK_TRANSFER | CARD)
  monthlyLimit?: number;        // 엔터프라이즈: 월 사용량
  monthlyPrice?: number;        // 엔터프라이즈: 월 이용료
  contractName?: string;        // 엔터프라이즈: 계약명
}

export interface PlanChangeResponse {
  success: boolean;
  message: string | null;
  effectiveDate: string;          // 적용 예정일 (ISO date)
  changeType: ChangeType;
  paymentMethod?: PaymentMethod;  // 결제 방법 (CARD 또는 BANK_TRANSFER)
  amount?: number;                // 결제 금액 (프롤레이션 적용)
}

export interface PaymentRecord {
  id: number;
  plan: PlanCode;
  amount: number;
  paymentMethod: 'BANK_TRANSFER' | 'CARD';
  status: PaymentStatus;
  billingMonth: string;
  depositorName: string | null;
  createdAt: string;
  confirmedAt: string | null;
  cashReceiptNo?: string | null;
  cashReceiptIssuedAt?: string | null;
  cashReceiptIssued?: boolean;
  monthlyLimit?: number;        // 엔터프라이즈: 월 사용량
  monthlyPrice?: number;        // 엔터프라이즈: 월 이용료
  contractName?: string;        // 엔터프라이즈: 계약명
  contractId?: number;          // 엔터프라이즈: 계약 ID
  teamId?: number | null;       // 팀 결제인 경우 팀 ID (팀 엔터프라이즈 환불 API 호출에 필요)
  teamName?: string;            // 팀 결제인 경우 팀 이름
  teamRole?: 'OWNER' | 'MEMBER' | null; // 팀 결제인 경우 현재 사용자의 팀 내 역할 (환불 버튼 노출 여부 판단)
  canCancel?: boolean;         // 취소 가능 여부
  vatAmount?: number;           // 부가세 금액
  amountExcludingVat?: number;  // 공급가액
  isDowngrade?: boolean;        // 다운그레이드 예약과 짝을 이루는 결제인지 (청구주기 종료일까지 PENDING 유지가 정상)
  refundAmount?: number | null; // 환불된 경우 실제 환불 금액
  refundedAt?: string | null;   // 환불 처리(접수) 일시
  /** 환불 실제 완료(송금 완료) 일시. CARD는 즉시 채워지고, BANK_TRANSFER는 관리자 확인 전까지 null */
  refundCompletedAt?: string | null;
  /** 회원의 증빙 설정 유형 */
  receiptType?: ReceiptType | null;
  /** 발행 처리 상태 */
  receiptIssueStatus?: ReceiptIssueStatus | null;
  /** 발행 승인번호 */
  receiptIssueNo?: string | null;
  /** 발행 일시 */
  receiptIssuedAt?: string | null;
  /** 발행 실패 사유 */
  receiptIssueFailReason?: string | null;
}

export interface PaymentListResponse {
  content: PaymentRecord[];
  totalElements: number;
  totalPages: number;
  number: number;
}

// 관리자용
export interface AdminPaymentItem {
  id: number;
  userId: string;
  userName: string;
  userEmail: string;
  plan: PlanCode;
  amount: number;
  monthlyPrice?: number;        // 엔터프라이즈: 월 이용료
  monthlyLimit?: number;        // 엔터프라이즈: 월 사용량
  contractName?: string;        // 엔터프라이즈: 계약명
  paymentMethod: 'BANK_TRANSFER' | 'CARD';
  status: PaymentStatus;
  billingMonth: string;
  depositorName: string | null;
  createdAt: string;
  confirmedAt: string | null;
  cashReceiptNo?: string | null;
  cashReceiptIssuedAt?: string | null;
  cashReceiptIssued?: boolean;
  canCancel?: boolean;         // 취소 가능 여부
  /** 회원의 증빙 설정 유형 */
  receiptType?: ReceiptType | null;
  /** 발행 처리 상태 */
  receiptIssueStatus?: ReceiptIssueStatus | null;
  /** 발행 승인번호 */
  receiptIssueNo?: string | null;
  /** 발행 실패 사유 */
  receiptIssueFailReason?: string | null;
  /** 팀 정보 */
  teamName?: string | null;
  teamRole?: 'OWNER' | 'MEMBER' | null;
  /** 환불된 경우 실제 환불 금액 */
  refundAmount?: number | null;
  /** 환불 처리(접수) 일시 */
  refundedAt?: string | null;
  /** 환불 실제 완료(송금 완료) 일시. BANK_TRANSFER는 관리자 확인 전까지 null */
  refundCompletedAt?: string | null;
}

export interface AdminPaymentListResponse {
  content: AdminPaymentItem[];
  totalElements: number;
  totalPages: number;
  number: number;
}

/** P0-T02: PATCH /api/admin/payments/{id}/confirm 성공 응답 */
export interface ConfirmPaymentResponse {
  paymentId: number;
  appliedPlan: PlanCode;
  appliedAt: string;   // ISO datetime
  userId: string;
}

export interface CashReceiptIssueResponse {
  paymentId: number;
  cashReceiptNo: string;
  cashReceiptIssuedAt: string;
}

// ── 증빙 발행 타입 ──────────────────────────────────────────────

/** 증빙 유형: 현금영수증 / 세금계산서 / 발행안함 */
export type ReceiptType = 'CASH_RECEIPT' | 'TAX_INVOICE' | 'NONE';
/** 현금영수증 용도: 개인 소득공제용 / 사업자 지출증빙용 */
export type CashReceiptPurpose = 'PERSONAL_DEDUCTION' | 'BUSINESS_EXPENSE';
/** 증빙 발행 처리 상태 (BE entity/PATCH 요청과 동일한 값 - com.ibj.alttext_be.entity.ReceiptIssueStatus) */
export type ReceiptIssueStatus = 'NOT_ISSUED' | 'PENDING' | 'ISSUED' | 'FAILED' | 'CANCELLED';
/** 증빙 발행 결과 응답 전용 상태 (BE가 issue/reissue/getResult 응답에서만 별도로 매핑해 내려주는 값) */
export type ReceiptIssueResultStatus = 'SUCCESS' | 'FAILED' | 'PENDING' | 'CANCELLED';

/** 회원 증빙 설정 정보 (GET/POST /api/my/receipt-info) */
export interface ReceiptInfo {
  receiptType: ReceiptType;
  cashReceiptPurpose?: CashReceiptPurpose | null;
  phoneNumber?: string | null;        // 개인 소득공제용 휴대폰번호
  cashReceiptBizNo?: string | null;   // 사업자 지출증빙용 사업자번호
  taxInvoiceBizNo?: string | null;    // 세금계산서 사업자번호
  companyName?: string | null;        // 세금계산서 상호명
  ceoName?: string | null;            // 세금계산서 대표자명
  invoiceEmail?: string | null;       // 세금계산서 수신 이메일
}

/** B/E GET /api/my/receipt-info 응답 데이터 — 필드명이 save payload와 다르고 마스킹 처리됨 */
export interface MyReceiptInfoData {
  receiptType: ReceiptType;
  cashReceiptPurpose?: CashReceiptPurpose | null;
  cashReceiptNumberMasked?: string | null;  // 소득공제: 휴대폰(마스킹) / 지출증빙: 사업자번호(마스킹)
  bizRegNoMasked?: string | null;           // 세금계산서 사업자번호(마스킹)
  bizName?: string | null;
  bizCeoName?: string | null;
  taxInvoiceEmail?: string | null;
}

export interface MyReceiptInfoApiResponse {
  success: boolean;
  data?: MyReceiptInfoData;
  message?: string;
}

/** 관리자용 회원 증빙 설정 조회 응답 (GET /api/admin/users/{userId}/receipt-info) — 엔티티 직접 반환, 마스킹 없음 */
export interface AdminReceiptInfo {
  receiptType: ReceiptType;
  cashReceiptPurpose?: CashReceiptPurpose | null;
  cashReceiptNumber?: string | null;  // 개인 소득공제: 휴대폰번호 / 사업자 지출증빙: 사업자번호
  bizRegNo?: string | null;           // 세금계산서 사업자번호
  bizName?: string | null;            // 세금계산서 상호명
  bizCeoName?: string | null;         // 세금계산서 대표자명
  taxInvoiceEmail?: string | null;    // 세금계산서 수신 이메일
}

export interface AdminReceiptInfoResponse {
  success: boolean;
  data?: AdminReceiptInfo;
  message?: string;
}

/** 환불 API 요청/응답 타입 */
// client 응답 인터셉터가 {success, data} 래퍼의 data(객체)를 success/message와
// 함께 한 단계로 병합하므로(중첩 data 없음), 실제 unwrap된 형태로 선언한다.
export interface RefundResponse {
  refundAmount: number;    // 환불 금액 (원)
  refundMethod?: 'CARD' | 'BANK_TRANSFER'; // 환불 방식
  /** "SUCCESS"(카드, 즉시 완료) 또는 "PENDING"(계좌이체, 관리자 송금완료 확인 대기) */
  refundStatus?: string;
  teamStatus?: string;     // 팀 플랜 환불 시 "INACTIVE"
  success: boolean;
  message: string | null;
  /** 환불 실제 완료 일시. 카드는 즉시 채워지고, 계좌이체는 관리자 확인 전까지 null */
  refundCompletedAt?: string | null;
}

/** 프로레이션 계산 세부 정보 */
export interface CalculationDetails {
  startDate: string;
  endDate: string;
  formula: string;
}

/** 사업자번호 상태조회 결과 (GET /api/business/status?bizNo=) */
export interface BusinessStatusResult {
  found: boolean;
  bizStatus?: string | null;       // 계속사업자 / 휴업자 / 폐업자
  bizStatusCode?: string | null;   // 01: 계속, 02: 휴업, 03: 폐업
  message?: string | null;
}

/** 사업자번호 진위확인 결과 (POST /api/business/validate) */
export interface BusinessValidateResult {
  valid: boolean;
  message?: string | null;
}

/** 증빙 발행 결과 */
export interface ReceiptIssueResult {
  paymentId: number;
  receiptType: ReceiptType;
  /** 프론트 표시용으로 매핑된 값 (BE ReceiptIssueResultResponse.issueStatus) */
  issueStatus: ReceiptIssueResultStatus;
  /** entity 원본 상태값 - 목록(AdminPaymentItem/PaymentRecord)과 동일한 값 집합이라 item 갱신 시 이걸 써야 함 */
  receiptIssueStatus?: ReceiptIssueStatus | null;
  issueNo?: string | null;
  issuedAt?: string | null;
  failReason?: string | null;
}

// ── 엔터프라이즈 계약 타입 ─────────────────────────────────────

/** 엔터프라이즈 계약 상태 */
export type EnterpriseContractStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'SUSPENDED';

/** 엔터프라이즈 계약 정보 */
export interface EnterpriseContract {
  id: number;
  userId?: string;             // 개인 계약인 경우
  teamId?: number;             // 팀 계약인 경우
  contractName: string;
  monthlyLimit: number;
  monthlyPrice: number;
  startDate: string;            // ISO date
  endDate: string;              // ISO date
  status: EnterpriseContractStatus;
  notes?: string;
  createdAt: string;            // ISO datetime
  updatedAt: string;            // ISO datetime
  effectiveDate?: string | null;  // 다운그레이드 적용 예정일
  changeType?: 'UPGRADE' | 'DOWNGRADE' | 'NONE';  // 변경 유형
  vatAmount?: number;           // 부가세 금액
  amountExcludingVat?: number;  // 공급가액
  autoRenewal?: boolean;        // 자동 갱신 여부
  renewalNotificationSent?: boolean;  // 갱신 알림 발송 여부
  isDowngrade?: boolean;        // 다운그레이드 여부
  nextBillingAmount?: number;   // 다음 청구 금액 (월 금액)
  downgradeMessage?: string;    // 다운그레이드 안내 메시지
  downgradeEffectiveDate?: string | null;  // 다운그레이드 적용일
  estimatedDowngradeEffectiveDate?: string | null;  // 예상 다운그레이드 적용일
  paymentMethod?: string;       // 결제 방식
  proratedAmount?: number;      // 비례 계산 금액
}

/** 엔터프라이즈 계약 생성/수정 요청 */
export interface EnterpriseContractRequest {
  contractName: string;
  monthlyLimit: number;
  monthlyPrice: number;
  startDate: string;
  endDate: string;
  notes?: string;
  paymentMethod?: 'CARD' | 'BANK_TRANSFER';
  usage: number;
  autoRenewal?: boolean;
}

/** 엔터프라이즈 가격 계산 응답 */
export interface EnterprisePriceResponse {
  usage: number;
  price: number;
  unitPrice: number;
  tier: string;
}

/** 엔터프라이즈 계약 API 응답 */
export interface EnterpriseContractResponse {
  success: boolean;
  message: string | null;
  data: EnterpriseContract;
}

// ── 카드 결제 API ─────────────────────────────────────────────

export interface CardPaymentRequest {
  amount: number;
  orderId: string;
  orderName: string;
  teamId?: number | null;
}

export interface CardPaymentResponse {
  success: boolean;
  data: {
    paymentId: number;
    paymentKey: string;
    orderId: string;
    orderName: string;
    amount: number;
    status: string;
    cardCompany: string;
    cardNumber: string;
  };
}

export interface CardRegisterRequest {
  customerKey: string;  // 사용자 ID
  cardNumber: string;
  cardExpirationYear: string;  // YYYY
  cardExpirationMonth: string; // MM
  customerIdentityNumber: string;  // 생년월일 6자리 (YYMMDD) 또는 사업자등록번호 10자리
  cardPassword: string;  // 카드 비밀번호 앞 2자리
  cardOwnerName: string;
  isDefault: boolean;  // 기본 카드 여부
}

export interface CardRegisterResponse {
  success: boolean;
  data: {
    billingKey: string;
    cardCompany: string;
    cardNumberMasked: string;
    cardOwnerName: string;
  };
}

export interface CardInfo {
  id: number;
  userId: string;
  cardCompany: string;
  cardNumberMasked: string;
  cardOwnerName: string;
  cardExpirationYear: string;
  cardExpirationMonth: string;
  isDefault: boolean;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
}

export interface CardListResponse {
  success: boolean;
  data: CardInfo[];
}

// 백엔드 실제 응답: res.data가 직접 배열인 경우
export type CardListResponseActual = CardInfo[];

// ── 사용자 API ───────────────────────────────────────────────

export interface FreePlanInfo {
  planId: string;       // 'FREE'
  monthlyLimit: number; // e.g. 100
  name: string;
  description?: string;
}

export const planApi = {
  /** 현재 플랜 및 사용량 조회 */
  getMyPlan: () =>
    client.get<PlanInfo>('/my/plan'),

  /** 다운그레이드 정책 조회(인증 불필요) - TermsModal/PlanPage 문구 통일용 */
  getPlanPolicy: () =>
    client.get<PlanPolicy>('/public/plan-policy'),

  /** P0-T01: 구독 상태머신 조회 (GET /api/my/subscription) */
  getMySubscription: () =>
    client.get<SubscriptionInfo>('/my/subscription'),

  /** P0-T01: 구독 상태 변경 이력 조회 (GET /api/my/subscription/history) */
  getSubscriptionHistory: (params: { page: number; size: number }) =>
    client.get<SubscriptionHistoryResponse>('/my/subscription/history', { params }),

  /** 당월 AI 사용량 조회 */
  getMyUsage: () =>
    client.get<UsageInfo>('/my/usage'),

  /** 요금제 변경 요청 */
  changePlan: (data: PlanChangeRequest) =>
    client.post<PlanChangeResponse>('/my/plan/change', data),

  /** 다운그레이드 예약 취소 */
  cancelDowngrade: () =>
    client.post<{ success: boolean; message: string; data: null }>('/my/plan/cancel'),

  /** 결제 이력 조회 */
  getBillingHistory: (params: { page: number; size: number }) =>
    client.get<PaymentListResponse>('/my/billing', { params }),

  /** 결제 취소 */
  cancelPayment: (paymentId: number) =>
    client.patch<{ success: boolean; message: string }>(`/my/billing/${paymentId}/cancel`),

  /** 현금영수증 발급 처리 */
  issueCashReceipt: (paymentId: number) =>
    client.post<CashReceiptIssueResponse>(`/my/billing/${paymentId}/cash-receipt/issue`),

  /** 전체 플랜 목록 조회 (GET /api/plans) */
  getPlans: () =>
    client.get<FreePlanInfo[]>('/plans'),

  /** 무료 플랜 정보 조회 (GET /api/plans/free) */
  getFreePlan: () =>
    client.get<FreePlanInfo>('/plans/free'),

  /** 카드 결제 요청 (POST /api/payments/card) */
  requestCardPayment: (data: CardPaymentRequest) =>
    client.post<CardPaymentResponse>('/payments/card', data),

  /** 환불 요청 (POST /api/payments/{paymentId}/refund) - 계좌이체는 환불 계좌 정보 필수 */
  refundPayment: (paymentId: number, data?: RefundRequest) =>
    client.post<RefundResponse>(`/payments/${paymentId}/refund`, data),

  /** 환불 금액 조회 (GET /api/plans/refund-amount/{paymentId}) */
  getRefundAmount: (paymentId: number) =>
    client.get<RefundAmountResponse>(`/plans/refund-amount/${paymentId}`),

  /** 프로레이션 계산 (GET /api/plans/proration) */
  calculateProration: (targetPlan: string) =>
    client.get<NormalProrationResponse>('/plans/proration', { params: { newPlan: targetPlan } }),

  /** 카드 등록 (POST /api/payments/card/register) */
  registerCard: (data: CardRegisterRequest) =>
    client.post<CardRegisterResponse>('/payments/card/register', data),

  /** 등록된 카드 목록 조회 (GET /api/payments/cards) */
  getCards: () =>
    client.get<CardListResponse>('/payments/cards'),

  /** 카드 삭제 (DELETE /api/payments/cards/{cardId}) */
  deleteCard: (cardId: number) =>
    client.delete<{ success: boolean; message: string }>(`/payments/cards/${cardId}`),

  /** 요금제 변경 가능 여부 확인 (GET /api/plans/can-change) */
  canChangePlan: () =>
    client.get<{ success: boolean; message?: string; data: { canChange: boolean } }>('/plans/can-change'),

  /** 청구서 다운로드 (GET /api/invoices/{paymentId}/download) */
  downloadInvoice: (paymentId: number) =>
    client.get(`/invoices/${paymentId}/download`, { responseType: 'blob' }),
};

// ── 엔터프라이즈 API ─────────────────────────────────────────────

export const enterpriseApi = {
  /** 엔터프라이즈 가격 계산 (GET /api/enterprise/price?usage=) */
  calculatePrice: (usage: number) =>
    client.get<EnterprisePriceResponse>(
      '/enterprise/price',
      { params: { usage } }
    ),

  /** 엔터프라이즈 프롤레이션 계산 (POST /api/user/enterprise/calculate-proration) */
  calculateProration: (request: ProrationRequest) =>
    client.post<EnterpriseProrationResponse>('/user/enterprise/calculate-proration', request),

  /** 팀 엔터프라이즈 프롤레이션 계산 (POST /api/teams/{teamId}/enterprise/calculate-proration) */
  calculateTeamProration: (teamId: number, request: ProrationRequest) =>
    client.post<EnterpriseProrationResponse>(`/teams/${teamId}/enterprise/calculate-proration`, request),

  /** 엔터프라이즈 환불 금액 조회 (GET /api/enterprise/refund-amount/{contractId}) */
  getRefundAmount: (contractId: number) =>
    client.get<RefundAmountResponse>(`/enterprise/refund-amount/${contractId}`),

  /** 엔터프라이즈 환불 요청 (POST /api/enterprise/refund/{contractId}) - 계좌이체는 환불 계좌 정보 필수 */
  requestRefund: (contractId: number, data: RefundRequest) =>
    client.post<RefundResponse>(
      `/enterprise/refund/${contractId}`,
      data
    ),

  /** 개인 엔터프라이즈 계약 생성 (POST /api/my/enterprise-contract) */
  createMyContract: (data: EnterpriseContractRequest) =>
    client.post<EnterpriseContractResponse>('/my/enterprise-contract', data),

  /** 개인 엔터프라이즈 계약 조회 (GET /api/my/enterprise-contract) */
  getMyContract: () =>
    client.get<EnterpriseContract | null>(
      '/my/enterprise-contract'
    ),

  /** 개인 엔터프라이즈 계약 수정 (PATCH /api/my/enterprise-contract/{id}) */
  updateMyContract: (id: number, data: EnterpriseContractRequest) =>
    client.patch<EnterpriseContractResponse>(`/my/enterprise-contract/${id}`, data),

  /** 팀 엔터프라이즈 계약 생성 (POST /api/teams/{teamId}/enterprise-contract) */
  createTeamContract: (teamId: number, data: EnterpriseContractRequest) =>
    client.post<EnterpriseContractResponse>(`/teams/${teamId}/enterprise-contract`, data),

  /** 팀 엔터프라이즈 계약 조회 (GET /api/teams/{teamId}/enterprise-contract) */
  getTeamContract: (teamId: number) =>
    client.get<EnterpriseContract | null>(
      `/teams/${teamId}/enterprise-contract`
    ),

  /** 팀 엔터프라이즈 계약 수정 (PATCH /api/teams/{teamId}/enterprise-contract/{id}) */
  updateTeamContract: (teamId: number, id: number, data: EnterpriseContractRequest) =>
    client.patch<EnterpriseContractResponse>(`/teams/${teamId}/enterprise-contract/${id}`, data),

  /** 팀 엔터프라이즈 환불 금액 조회 (GET /api/teams/{teamId}/enterprise-contract/{contractId}/refund-amount) */
  getTeamRefundAmount: (teamId: number, contractId: number) =>
    client.get<RefundAmountResponse>(`/teams/${teamId}/enterprise-contract/${contractId}/refund-amount`),

  /** 팀 엔터프라이즈 환불 요청 (POST /api/teams/{teamId}/enterprise-contract/{contractId}/refund) - 팀장만 가능, 계좌이체는 환불 계좌 정보 필수 */
  requestTeamRefund: (teamId: number, contractId: number, data: RefundRequest) =>
    client.post<RefundResponse>(
      `/teams/${teamId}/enterprise-contract/${contractId}/refund`,
      data
    ),
};

// ── 관리자 API ───────────────────────────────────────────────

export const adminPaymentApi = {
  /** 입금 대기 목록 (전체 상태 조회 가능). refundPendingOnly=true면 status 무관하게 계좌이체 환불 송금대기 건만 조회 */
  getList: (params: { page: number; size: number; status?: PaymentStatus; refundPendingOnly?: boolean }) =>
    client.get<AdminPaymentListResponse>('/admin/payments', { params }),

  /** 입금 확인 처리 — P0-T02: 응답에 paymentId, appliedPlan, appliedAt, userId 포함 */
  confirm: (id: number) =>
    client.patch<ConfirmPaymentResponse>(`/admin/payments/${id}/confirm`),

  /** 결제 취소 */
  cancel: (id: number) =>
    client.patch(`/admin/payments/${id}/cancel`),

  /** 계좌이체 환불 송금완료 처리 */
  confirmRefund: (id: number) =>
    client.patch<{ paymentId: number; refundCompletedAt: string; userId: string }>(`/admin/payments/${id}/confirm-refund`),
};

/**
 * Workspace 네이티브 결제(workspace_payment_records) - 관리자 조회/확인/환불(2026-09-09
 * 신설, 환불은 V142 2026-09-10 추가). 위 adminPaymentApi(레거시 payment_records)와
 * 완전히 별개 데이터 소스 - 8/3 컷오버 이후 정기결제(WorkspaceRecurringBillingScheduler)가
 * 실제로 쌓는 테이블은 이쪽이다. 증빙(세금계산서/현금영수증) 개념은 아직 이 테이블에
 * 없어 AdminPaymentItem과 의도적으로 다른 축소 타입을 쓴다.
 */
export type WorkspaceNativePaymentStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED';

export interface WorkspaceNativePaymentItem {
  id: number;
  workspaceId: number;
  workspaceName: string | null;
  personal: boolean;
  ownerUserId: string | null;
  paymentType: 'PLAN_CHANGE' | 'RECURRING' | 'OVERAGE';
  plan: PlanCode;
  amount: number;
  paymentMethod: 'BANK_TRANSFER' | 'CARD';
  status: WorkspaceNativePaymentStatus;
  billingMonth: string;
  requestedBy: string;
  createdAt: string;
  confirmedAt: string | null;
  confirmedBy: string | null;
  canConfirm: boolean;
  /** V142(2026-09-10 신설) - 환불 관련 필드 */
  refundAmount: number | null;
  refundedAt: string | null;
  refundCompletedAt: string | null;
  canRefund: boolean;
  canConfirmRefundTransfer: boolean;
}

export interface WorkspaceNativePaymentListResponse {
  content: WorkspaceNativePaymentItem[];
  totalElements: number;
  totalPages: number;
  number: number;
}

export const adminNativePaymentApi = {
  /** Workspace 네이티브 결제 목록 */
  getList: (params: { page: number; size: number; status?: WorkspaceNativePaymentStatus }) =>
    client.get<WorkspaceNativePaymentListResponse>('/admin/native-payments', { params }),

  /** 계좌이체 입금 확인 — CARD 건은 이미 자동확정되어 대상이 아님(canConfirm=false) */
  confirm: (id: number) =>
    client.patch(`/admin/native-payments/${id}/confirm`),

  /** 환불(V142, 2026-09-10 신설) - 카드는 Toss 부분취소로 즉시 완료, 계좌이체는 접수만
   * 되고 아래 confirmRefundTransfer로 송금완료를 별도 확인해야 한다. 레거시 환불과
   * 달리 플랜/워크스페이스 상태는 건드리지 않는다(이 결제 1건만 환불). */
  refund: (id: number, reason?: string) =>
    client.post<{ success: boolean; message: string }>(
      `/admin/native-payments/${id}/refund`, null, { params: reason ? { reason } : undefined }
    ),

  /** 계좌이체 환불 송금완료 처리(V142, 위 refund의 짝) */
  confirmRefundTransfer: (id: number) =>
    client.patch<{ success: boolean; message: string }>(`/admin/native-payments/${id}/confirm-refund`),
};

/**
 * 유료회원 결제 현황 모니터링(2026-09-12 신설) - 개인 워크스페이스 재조정 버그로 결제요청/
 * 자동취소가 조용히 누락됐던 사고 재발 방지용. 위 adminNativePaymentApi와 달리 결제 기록이
 * 아니라 회원(워크스페이스) 단위로 훑어, 이번 달 결제기록이 아예 없는 이상 상태(billingGap)를
 * 목록에서 바로 식별할 수 있게 한다. PlanController(/api/admin/member-billing-status)가
 * 항상 활성화된 채널이라 workspace.api.enabled 플래그와 무관하다.
 */
export interface WorkspaceMemberBillingStatusItem {
  workspaceId: number;
  ownerUserId: string;
  ownerName: string | null;
  ownerEmail: string | null;
  personal: boolean;
  teamName: string | null;
  plan: PlanCode;
  price: number;
  status: 'ACTIVE' | 'INACTIVE' | 'PAST_DUE';
  pastDueSince: string | null;
  billingCycleDay: number | null;
  paymentMethod: 'BANK_TRANSFER' | 'CARD' | null;
  currentBillingMonth: string;
  currentMonthPaymentStatus: 'NONE' | 'PENDING' | 'CONFIRMED' | 'CANCELLED';
  currentMonthPaymentCreatedAt: string | null;
  currentMonthPaymentConfirmedAt: string | null;
  billingGap: boolean;
}

export const adminMemberBillingStatusApi = {
  getList: () => client.get<WorkspaceMemberBillingStatusItem[]>('/admin/member-billing-status'),

  /** 계좌이체 입금요청 수동 재발송(2026-09-12 신설) - 이번 달 결제기록이 CANCELLED로
   * 끝났거나 애초에 생성되지 않아 고객이 입금할 대상이 없는 워크스페이스에 새 PENDING
   * 요청을 재발급한다. 이번 달 이미 PENDING/CONFIRMED가 있으면 409. */
  reissuePaymentRequest: (workspaceId: number) =>
    client.post<{ success: boolean; message: string }>(
      `/admin/member-billing-status/${workspaceId}/reissue-payment-request`
    ),
};

/**
 * 엔터프라이즈 계약 현황 모니터링(2026-09-12 신설) - 위 유료회원 결제 현황이 ENTERPRISE
 * 플랜을 제외하는 것과 별개로, 계약 단위 데이터(UserEnterpriseContract/
 * TeamEnterpriseContract)를 전용으로 훑는다. 계약 SUSPENDED 여부와 Workspace PAST_DUE
 * 여부가 서로 어긋나면 statusMismatch=true.
 */
export interface EnterpriseContractStatusItem {
  contractId: number;
  contractType: 'USER' | 'TEAM';
  ownerUserId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  teamName: string | null;
  contractName: string;
  monthlyLimit: number;
  monthlyPrice: number;
  contractStatus: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'SUSPENDED';
  startDate: string;
  endDate: string;
  workspaceStatus: 'ACTIVE' | 'INACTIVE' | 'PAST_DUE' | null;
  statusMismatch: boolean;
}

export const adminEnterpriseContractStatusApi = {
  getList: () => client.get<EnterpriseContractStatusItem[]>('/admin/enterprise-contract-status'),
};

// ── 증빙 신청 API (회원) ────────────────────────────────────────

export const receiptInfoApi = {
  /** 저장된 증빙 설정 조회 (미설정 시 404, 마스킹 해서 직접 반환 — cashReceiptNumberMasked, bizRegNoMasked) */
  get: () => client.get<MyReceiptInfoData>('/my/receipt-info'),
  /** 증빙 설정 저장/수정 */
  save: (data: ReceiptInfo) => client.post<ReceiptInfo>('/my/receipt-info', data),
  /** 사업자번호 상태조회 (계속/휴업/폐업, GET /api/business/status?bizNo=) */
  checkBusinessStatus: (bizNo: string) =>
    client.get<BusinessStatusResult>('/business/status', { params: { bizNo } }),
  /** 사업자번호 진위확인 (사업자번호 + 상호명 + 대표자명 일치 여부, POST /api/business/validate) */
  validateBusiness: (bizRegNo: string, bizName: string, bizCeoName: string) =>
    client.post<BusinessValidateResult>('/business/validate', { bizRegNo, bizName, bizCeoName }),
};

/**
 * 관리자 증빙 발행 API
 *
 * @recommendation 즉시 발행 솔루션으로 팝빌(https://www.popbill.com) 권장.
 *   - 현금영수증·세금계산서 모두 지원, 건당 비용 저렴(현금영수증 약 10원/건)
 *   - 공식 Java/Spring SDK 제공, 국내 레퍼런스 다수, 안정적
 *   - 대안: 이지빌(https://www.ezb.co.kr), 세금계산서나라(https://www.taxbillnara.com)
 */
export const adminReceiptApi = {
  /** 증빙 발행 요청 (POST /api/admin/payments/{id}/issue-receipt) */
  issue: (paymentId: number) =>
    client.post<ReceiptIssueResult>(`/admin/payments/${paymentId}/issue-receipt`),
  /** 발행 결과 조회 (GET /api/admin/payments/{id}/receipt-result) */
  getResult: (paymentId: number) =>
    client.get<ReceiptIssueResult>(`/admin/payments/${paymentId}/receipt-result`),
  /** 실패 건 재발행 요청 (POST /api/admin/payments/{id}/reissue-receipt) */
  reissue: (paymentId: number) =>
    client.post<ReceiptIssueResult>(`/admin/payments/${paymentId}/reissue-receipt`),
  /** 회원 증빙 설정 조회 (관리자용) — 엔티티 직접 반환 */
  getUserReceiptInfo: (userId: string) =>
    client.get<AdminReceiptInfo>(`/admin/users/${userId}/receipt-info`),
  /**
   * 발급 상태 변경 (PATCH /api/admin/payments/{id}/receipt-status)
   * status=ISSUED로 바꿀 때는 issueNo(실제 승인번호)를 함께 보내야 함 (이미 저장된 번호가 있으면 생략 가능)
   */
  updateStatus: (paymentId: number, status: ReceiptIssueStatus, issueNo?: string) =>
    client.patch<{ paymentId: number; receiptIssueStatus: ReceiptIssueStatus; receiptIssueNo: string | null }>(
      `/admin/payments/${paymentId}/receipt-status`, { status, issueNo }
    ),
};
